import { Ban, Undo2 } from 'lucide-react';
import { useVatLang } from './VatLang';
import type { ExclusionAction, VatEntryRow } from '../../types/vatReporting';
import type { ExclusionTarget } from './EntryExclusionDialog';

/**
 * How a manually decided posting looks in a table, and what can be done to it - in one place.
 *
 * SHARED BY THE POSTEN TAB AND THE DRILL MODAL because the two show the same rows and a person
 * moves between them. A struck-through line that meant "excluded" in one table and something
 * else in the other would be worse than not marking it at all.
 */

const periodName = (y: number, m: number) => `${y}-${String(m).padStart(2, '0')}`;

/**
 * Whether a decision keeps the posting out of THE MONTH ON SCREEN.
 *
 * NOT THE SAME AS "carries a decision", and getting that wrong is the easy mistake: a posting
 * carried INTO this month has an exclusion row saying `included`, and it is fully part of this
 * return - striking it through would tell a reader the opposite of the truth about the figure
 * they are looking at. Only a decision whose target is some OTHER month (or none at all) takes
 * the posting out of this one.
 */
export function isExcludedFrom(row: VatEntryRow, year: number, month: number): boolean {
    const e = row.exclusion;
    if (!e) return false;
    if (e.state !== 'included') return true;                 // deferred or dropped: out
    return !(e.includedInYear === year && e.includedInMonth === month);
}

/** Struck through and greyed - the row is not in this month's declarations. */
export function exclusionRowClass(row: VatEntryRow, year: number, month: number): string {
    if (!row.exclusion) return '';
    return isExcludedFrom(row, year, month)
        ? 'line-through text-gray-400'
        // Carried in: it IS in this return, and it arrived from somewhere else. Tinted rather
        // than struck, so the two states can never be read for one another.
        : 'bg-blue-50/60';
}

/** The reason, the author and where the posting went - the whole decision in one tooltip. */
export function useExclusionTitle() {
    const { t } = useVatLang();
    return (row: VatEntryRow): string | undefined => {
        const e = row.exclusion;
        if (!e) return undefined;
        const parts = [
            t(`excl.state.${e.state}`),
            e.comment,
            t('excl.tooltipBy', {
                who: e.createdBy,
                when: new Date(e.createdAt).toLocaleDateString('de-CH'),
                period: periodName(e.originYear, e.originMonth),
            }),
        ];
        if (e.state === 'included' && e.includedInYear !== null && e.includedInMonth !== null) {
            parts.push(t('excl.tooltipInto', {
                period: periodName(e.includedInYear, e.includedInMonth),
            }));
        }
        if (e.resolutionComment) parts.push(e.resolutionComment);
        return parts.join(' · ');
    };
}

export function exclusionTargetFor(row: VatEntryRow): ExclusionTarget {
    return {
        entryNo: row.entryNo,
        documentNo: row.documentNo || null,
        counterpartyName: row.counterpartyName,
        kennzahl: row.kennzahl,
        contribution: row.contribution,
        originYear: row.exclusion?.originYear,
        originMonth: row.exclusion?.originMonth,
        originComment: row.exclusion?.comment,
    };
}

/**
 * The per-row action.
 *
 * ONE BUTTON, NOT A MENU. A row with no decision offers "nicht melden"; a row that carries one
 * offers to withdraw it. Everything else a deferral can become - carried into a month, closed
 * for good - belongs to the Nachzügler panel, where the target month is on screen and the
 * choice has context. Offering "übernehmen" here would ask which month without showing one.
 *
 * EVERY BRANCH RENDERS AN inline-block. CSS text-decoration propagates from the row down into
 * inline descendants and a child cannot switch it off - so on a struck-through row this cell
 * would come out struck too, and an action reads as unavailable when it is struck. An atomic
 * inline-level box (inline-block) is the documented exception the decoration does not enter.
 */
export function ExclusionActionCell({
    row, canEdit, periodLocked, onAct,
}: {
    row: VatEntryRow;
    canEdit: boolean;
    periodLocked: boolean;
    onAct: (action: ExclusionAction, row: VatEntryRow) => void;
}) {
    const { t } = useVatLang();

    // The settlement mirror is not part of any declaration to begin with - excluding it would
    // be a decision about a posting `source.ts` already leaves out of every total.
    if (row.kind === 'Ausgleich' || !canEdit) {
        return <span className="inline-block text-xs text-gray-300">—</span>;
    }
    if (periodLocked) {
        return (
            <span className="inline-block text-xs text-gray-400" title={t('excl.periodFiled')}>
                {t('excl.periodFiledShort')}
            </span>
        );
    }

    if (row.exclusion) {
        return (
            <button onClick={() => onAct('reset', row)}
                    title={t('excl.resetHint')}
                    className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 no-underline">
                <Undo2 className="h-3.5 w-3.5" />{t('excl.reset')}
            </button>
        );
    }

    return (
        <button onClick={() => onAct('exclude', row)}
                title={t('excl.excludeHint')}
                className="inline-flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900">
            <Ban className="h-3.5 w-3.5" />{t('excl.exclude')}
        </button>
    );
}
